<script lang="ts">
import { createWidgetMixin } from "vue-instantsearch/vue3/es";
import { connectSearchBox } from "instantsearch.js/es/connectors";
import { autocomplete } from "@algolia/autocomplete-js";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
import porscheSvg from "../../../images/porsche.svg";
import ferrariSvg from "../../../images/ferrari.svg";
import audiSvg from "../../../images/audi.svg";
import mercedesSvg from "../../../images/mercedes.svg";
import mclarenSvg from "../../../images/mclaren.svg";
import lamborghiniSvg from "../../../images/lamborghini.svg";

// Imports des helpers
import {
    setInstantSearchUiState,
    updateInputValue,
    toMarketplace,
    updateAlgoliaState,
} from "./helpers/navigation";
import { createTypesenseQuerySuggestionsPlugin } from "./helpers/plugins";
import {
    displayRecentSearches,
    createAutocompleteClient,
    createBrandData,
} from "./helpers/utils";
import {
    clearHomeSearchPanelOpenState,
    clearPillSearchPanelOpenState,
    resolveSearchPanelLiantOptions,
} from "../../shared/pill-search-panel-liant";
import { installAutocompleteScrollGuard } from "../../shared/autocomplete-scroll-guard";

const page = usePage();

import "@algolia/autocomplete-theme-classic";
import { VNode } from "vue";
import { useI18n } from "vue-i18n";
import { router, usePage } from "@inertiajs/vue3";

export default {
    mixins: [createWidgetMixin({ connector: connectSearchBox })],
    props: {
        searchClient: {
            type: Object,
            required: true,
        },
        indexName: {
            type: String,
            required: true,
        },
        identifiant: {
            type: String,
            required: true,
        },
        research: {
            type: String,
            required: false,
            default: "",
        },
        placeholder: {
            type: String,
            required: false,
            default: "",
        },
    },
    mounted() {
        installAutocompleteScrollGuard();
        const { t } = useI18n();
        const searchClient = this.searchClient;
        const indexName = this.indexName;
        const identifiant = this.identifiant;
        const research = this.research;

        // Créer un client dédié à l'autocomplete avec configuration flexible
        const autocompleteSearchClient = createAutocompleteClient(
            this.$page.props.search,
            this.searchClient,
        );

        const { instantSearchInstance } = this;

        // Initialiser les fonctions utilitaires
        const setUiState = setInstantSearchUiState(
            instantSearchInstance,
            indexName,
        );
        const updateInput = updateInputValue(identifiant);
        const updateAlgolia = updateAlgoliaState(
            page,
            instantSearchInstance,
            indexName,
        );

        const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
            key: `instantsearch-${identifiant}`,
            limit: 3,
            transformSource({ source }) {
                return {
                    ...source,
                    // Condition pour ne pas afficher les recherches récentes quand on tape
                    getItems(params) {
                        if (params.query && params.query.length > 0) {
                            return []; // Retourner une liste vide si on tape une requête
                        }
                        return source.getItems(params);
                    },
                    onSelect({ item }) {
                        if (page.url.startsWith("/marketplace")) {
                            updateInput(item.label);
                            setUiState({ query: item.label });
                        } else {
                            toMarketplace({ query: item.label });
                        }
                    },
                    templates: {
                        ...source.templates,
                        item(params) {
                            const { item, html } = params;
                            const itemNode = source.templates.item(
                                params,
                            ) as unknown as VNode;

                            if (itemNode && itemNode.props) {
                                const trash =
                                    itemNode.props.children[1].props
                                        .children[0];
                                trash.props.children = html`
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M4.64962 4L4 4.64962L7.35038 8L4 11.3504L4.64962 12L8 8.64962L11.3504 12L12 11.3504L8.64962 8L12 4.64962L11.3504 4L8 7.35038L4.64962 4Z"
                                            fill="#D9DCDC"
                                        />
                                    </svg>
                                `;
                                return html`<span class="aa-ItemLink">
                                    ${item.label} ${trash}
                                </span>`;
                            } else {
                                return html`<span class="aa-ItemLink">
                                    ${item.label}
                                </span>`;
                            }
                        },
                    },
                };
            },
        });

        const displayRecentSearchesCount =
            displayRecentSearches(recentSearchesPlugin);

        const querySuggestionsPlugin = createTypesenseQuerySuggestionsPlugin(
            autocompleteSearchClient,
            indexName,
            page,
            setUiState,
            toMarketplace,
        );

        // Données des marques
        const brandImages = {
            porsche: porscheSvg,
            ferrari: ferrariSvg,
            audi: audiSvg,
            mercedes: mercedesSvg,
            mclaren: mclarenSvg,
            lamborghini: lamborghiniSvg,
        };
        const brandData = createBrandData(brandImages);
        const panelLiant = resolveSearchPanelLiantOptions(identifiant);

        this.autocompleteInstance = autocomplete({
            container: `#${identifiant}`,
            placeholder: this.placeholder,
            detachedMediaQuery: "none",
            ...(panelLiant ?? {}),
            initialState: { query: research ?? "" },
            onSubmit({ state }) {
                if (page.url.startsWith("/marketplace")) {
                    setUiState({ query: state.query });
                } else {
                    toMarketplace({ query: state.query });
                }
            },
            onReset() {
                if (page.url.startsWith("/marketplace")) {
                    updateInput("");
                    setUiState({ query: "" });
                }
            },
            openOnFocus: true,
            plugins: [recentSearchesPlugin, querySuggestionsPlugin],
            getSources() {
                return [
                    {
                        sourceId: "brands",
                        getItems({ query }) {
                            // Ne pas afficher les marques si on tape une requête
                            if (query && query.length > 0) {
                                return [];
                            }

                            return brandData.filter(({ label }) =>
                                label
                                    .toLowerCase()
                                    .includes(query.toLowerCase()),
                            );
                        },
                        templates: {
                            item({ item, html }) {
                                const brandItem = item as {
                                    label: string;
                                    img: string;
                                };
                                return html`<button
                                    class="aa-ItemLink"
                                    onclick=${() => updateAlgolia(brandItem)}
                                >
                                    <img
                                        class="aa-Item--icon"
                                        src="${brandItem.img}"
                                        alt="${brandItem.label}"
                                    />
                                    ${brandItem.label}
                                </button>`;
                            },
                        },
                    },
                ];
            },
            render({ elements, render, html, state }, root) {
                const { recentSearchesPlugin, querySuggestionsPlugin, brands } =
                    elements;

                if (!state.query) {
                    render(
                        html`<div class="aa-PanelLayout aa-Panel--scrollable">
                            <div class="aa-PanelSections">
                                ${displayRecentSearchesCount() > 0
                                    ? html`<div>
                                          <div class="aa-SourceHeader">
                                              <span
                                                  class="aa-SourceHeaderTitle"
                                              >
                                                  ${t("recentSearches")}
                                              </span>
                                          </div>
                                          ${recentSearchesPlugin}
                                      </div>`
                                    : ""}
                                <div>
                                    <div class="aa-SourceHeader">
                                        <span class="aa-SourceHeaderTitle"
                                            >${t("iconicBrands")}</span
                                        >
                                    </div>
                                    <div class="aa-PanelSectionBrands">
                                        ${brands}
                                    </div>
                                </div>
                            </div>
                        </div>` as unknown as VNode,
                        root,
                    );
                } else {
                    render(
                        html`<div class="aa-PanelLayout aa-Panel--scrollable">
                            <div class="aa-PanelSections">
                                <div>
                                    <Fragment>
                                        ${querySuggestionsPlugin || ""}
                                    </Fragment>
                                </div>
                            </div>
                        </div>` as unknown as VNode,
                        root,
                    );
                }
            },
        });

        this.$nextTick(() => {
            setTimeout(() => {
                const container = this.$el as HTMLElement;
                const form = container.querySelector<HTMLElement>(".aa-Form");
                const input = container.querySelector<HTMLInputElement>(".aa-Input");

                if (form && input) {
                    const searchBtn = document.createElement("button");
                    searchBtn.type = "button";
                    searchBtn.className = "aa-SearchButton";
                    searchBtn.textContent = "Rechercher";
                    searchBtn.addEventListener("click", () => input.focus());
                    form.appendChild(searchBtn);
                }
            }, 100);
        });
    },

    beforeUnmount() {
        this.autocompleteInstance?.destroy();
        clearPillSearchPanelOpenState();
        clearHomeSearchPanelOpenState();
    },
    methods: {
        /**
         * Créer un client de recherche optimisé pour l'autocomplete
         */
        createAutocompleteClient() {
            const searchConfig = this.$page.props.search;
            return createAutocompleteClient(searchConfig, this.searchClient);
        },
    },
};
</script>

<template>
    <div :id="identifiant" class="autocomplete"></div>
</template>

<style lang="scss">
.autocomplete {
    flex-grow: 1;
    border-radius: 4px;
    @include large-up {
        border: none;
        min-width: 50px;
        border-radius: 0;
    }

    &__header {
        display: none;
        border-radius: 4px;
        border: 1px solid #d8dcdc;
        min-width: 800px;
        @include large-up {
            display: block;
        }
        &--mobile {
            display: block;
            min-width: auto;
            width: 90%;
            margin: 0 auto;
        }
    }
}
.aa-Panel {
    z-index: 110;
    margin-top: -2px;
    padding: 0 1rem 1rem 1rem;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    box-shadow: none;
    border: 2px solid $grey-card;

    @media (max-width: calc(50rem - 1px)) {
        font-size: 0.875rem;
    }

    // Portaled to body — above the sticky header (z-index 100) so it isn't masked
    &.aa-Panel--pill-search {
        z-index: 110;
    }
}

.aa-PanelLayout {
    padding: 0;
}

.aa-SourceHeader {
    padding-top: 1rem;
    margin-top: 0;
}

.aa-SourceHeaderTitle {
    color: black;
    font-size: 1rem;

    @media (max-width: $breakpoint-l) {
        font-weight: 500;
    }
}
.aa-List {
    display: flex;
    margin-top: 0.75rem;

    @media (max-width: $breakpoint-l) {
        display: block;
    }

    .aa-Item {
        display: flex;
        justify-content: space-between;
        border-radius: 4px;
        border: 1px solid rgba(217, 220, 220, 0.5);
        margin-right: 0.5rem;
        padding: 0.75rem 0.5rem;
        @media (max-width: $breakpoint-l) {
            border: none;
            padding: 0.25rem 0 0.5rem 0;
            min-height: auto;
        }

        &--icon {
            margin-right: 0.5rem;
            max-height: 22px;

            @media (max-width: $breakpoint-l) {
                display: none;
            }
        }
        .aa-ItemContent {
            .aa-ItemIcon {
                display: none;
            }
        }
        .aa-ItemActions {
            @media (max-width: $breakpoint-l) {
                display: none;
            }
        }
    }
}

.aa-ItemActionButton {
    padding: 0;
    svg {
        margin: 0 0 0 0.5rem;
    }
}

.aa-PanelSectionBrands {
    > .aa-Source > .aa-List > .aa-Item {
        padding: 0;
        > .aa-ItemLink {
            cursor: pointer;
            padding: 0.5rem;
            @media (max-width: $breakpoint-l) {
                padding: 0.25rem 0 0.5rem 0;
            }
        }
    }
}

/*Style spécifique pour l'affichage des suggestions de recherche
on cible l'id car la classe est la même pour les recherches récentes*/
[id^="autocomplete-"][id$="-querySuggestionsPlugin-list"] {
    display: flex;
    flex-direction: column;
    .aa-Item {
        border: none;
        > .aa-ItemContentTitle {
            color: rgba(0, 0, 0, 0.2);
            font-weight: 400;
            > mark {
                font-weight: 700;
                color: black;
                background-color: transparent;
            }
        }
    }
}

.aa-Label svg {
    color: black;
}

.aa-Form {
    border: none;
    position: relative;

    &:focus-within {
        box-shadow: none;
    }
}

.aa-SearchButton {
    display: none;

    @include medium-up {
        display: block;
        position: absolute;
        right: 4px;
        top: 50%;
        transform: translateY(-50%);
        background-color: $dark-font;
        color: white;
        border: none;
        border-radius: 6px;
        padding: 0 1rem;
        height: 40px;
        font-family: $roboto;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        white-space: nowrap;
        z-index: 1;
    }
}
</style>
